/**
 * Consolidated reporting handlers for ReportingPageV2 stories
 * Includes all necessary MSW handlers with consistent fund IDs
 */
import { getGetFundCasSettingsByFundIdMockHandler } from "@vega-api/generated/cas-settings-v1/cas-settings-v1.msw";
import { getGetLatestFundDailyValuationMockHandler } from "@vega-api/generated/fund-daily-valuations-v1/fund-daily-valuations-v1.msw";
import { getGetLatestFundDailyValuationResponseMock } from "@vega-api/generated/fund-daily-valuations-v1/fund-daily-valuations-v1-mocks.msw";
import { getGetScheduleOfInvestmentsMockHandler } from "@vega-api/generated/fund-holding-v1/fund-holding-v1.msw";
import {
  getExportFundHoldingsMockHandler,
  getGetFundHoldingsMockHandler,
} from "@vega-api/generated/fund-holdings-v1/fund-holdings-v1.msw";
import { getGetFundPerformanceMetricsMockHandler } from "@vega-api/generated/fund-time-series-v1/fund-time-series-v1.msw";
import { getGetInvestmentMockHandler } from "@vega-api/generated/investment-v1/investment-v1.msw";
import { getGetCasInvestorDocumentsMockHandler } from "@vega-api/generated/investor-document-cas-v1/investor-document-cas-v1.msw";
import { getGetFundPerformanceMetricsTableMockHandler } from "@vega-api/generated/metric-v2/metric-v2.msw";
import { type ApiResponseCasSetting } from "@vega-api/generated/types/apiResponseCasSetting";
import { type ApiResponseInvestment } from "@vega-api/generated/types/apiResponseInvestment";
import { type ApiResponseScheduleOfInvestments } from "@vega-api/generated/types/apiResponseScheduleOfInvestments";

import { mockSOIdata } from "./mockSOIdata";

// Consistent fund ID across all handlers - matches investment mock
const REPORTING_FUND_ID = "123e4567-e89b-12d3-a456-426614174003";

// Investment data with matching fund ID
export const getReportingInvestmentResponse = (): ApiResponseInvestment => ({
  data: {
    id: "123e4567-e89b-12d3-a456-426614174000",
    investingEntityId: "123e4567-e89b-12d3-a456-426614174001",
    investingEntityName: "Investment Entity",
    limitedPartnerId: "123e4567-e89b-12d3-a456-426614174002",
    fundId: REPORTING_FUND_ID,
    fundName: "Apollo Fund X",
    fundAssetClass: "PRIVATE_EQUITY",
    fundVehicleId: "123e4567-e89b-12d3-a456-426614174004",
    fundVehicleName: "Vehicle X",
    shareClassId: "123e4567-e89b-12d3-a456-426614174005",
    shareClassName: "Share Class X",
    initialInvestmentDate: "2024-12-12",
    pledged: true,
    externalId: "INV-1234567890",
    currency: "USD",
  },
  meta: {},
  errors: {},
});

// CAS Settings with matching fund ID
export const getReportingCasSettingsResponse = (): ApiResponseCasSetting => ({
  data: {
    fundId: REPORTING_FUND_ID,
    fundName: "Apollo Global Fund",
    templateKey: "APOLLO",
    templateName: "Apollo Standard",
    isActive: true,
  },
  meta: {},
  errors: {},
});

export const ScheduleOfInvestmentsResponse =
  (): ApiResponseScheduleOfInvestments => ({
    data: mockSOIdata,
    meta: {},
    errors: {},
  });

const reportingHandlersBase = [
  getGetInvestmentMockHandler(getReportingInvestmentResponse()),
  getGetScheduleOfInvestmentsMockHandler(ScheduleOfInvestmentsResponse()),
  getGetFundHoldingsMockHandler(),
  getExportFundHoldingsMockHandler(),
  getGetFundPerformanceMetricsMockHandler(),
  getGetFundPerformanceMetricsTableMockHandler(),
  getGetFundCasSettingsByFundIdMockHandler(getReportingCasSettingsResponse()),
  getGetLatestFundDailyValuationMockHandler(
    getGetLatestFundDailyValuationResponseMock(),
  ),
];

export const getReportingPageHandlers = (options?: { excludeCas?: boolean }) =>
  options?.excludeCas === true
    ? reportingHandlersBase
    : [
        reportingHandlersBase[0],
        getGetCasInvestorDocumentsMockHandler(),
        ...reportingHandlersBase.slice(1),
      ];

export const reportingPageHandlers = getReportingPageHandlers();

export { REPORTING_FUND_ID };
